import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Gem, AlertTriangle, Lock, Factory, Cpu, Shield, Zap, TrendingUp, Globe } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import PricingDialog from "@/components/PricingDialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";

interface Material {
  name: string;
  symbol: string;
  riskLevel: "critical" | "high" | "medium";
  topProducers: { country: string; flag: string; share: number }[];
  applications: string[];
  disruption: string;
  supplyConcentration: number; // % controlled by top producer
}

const MATERIALS: Material[] = [
  {
    name: "Rare Earth Elements",
    symbol: "REE",
    riskLevel: "critical",
    topProducers: [
      { country: "China", flag: "🇨🇳", share: 60 },
      { country: "Myanmar", flag: "🇲🇲", share: 12 },
      { country: "Australia", flag: "🇦🇺", share: 8 },
    ],
    applications: ["Defense systems", "EVs", "Wind turbines"],
    disruption: "China export controls tightened Dec 2024 on Gallium, Germanium processing tech",
    supplyConcentration: 60,
  },
  {
    name: "Lithium",
    symbol: "Li",
    riskLevel: "critical",
    topProducers: [
      { country: "Australia", flag: "🇦🇺", share: 47 },
      { country: "Chile", flag: "🇨🇱", share: 24 },
      { country: "China", flag: "🇨🇳", share: 15 },
    ],
    applications: ["EV batteries", "Grid storage", "Electronics"],
    disruption: "Price volatility: 80% drop from 2022 peak, mine closures accelerating",
    supplyConcentration: 47,
  },
  {
    name: "Cobalt",
    symbol: "Co",
    riskLevel: "critical",
    topProducers: [
      { country: "DRC", flag: "🇨🇩", share: 73 },
      { country: "Indonesia", flag: "🇮🇩", share: 6 },
      { country: "Russia", flag: "🇷🇺", share: 5 },
    ],
    applications: ["Battery cathodes", "Superalloys", "Jet engines"],
    disruption: "DRC conflict zones & child labor concerns; EU due diligence rules tighten supply",
    supplyConcentration: 73,
  },
  {
    name: "Gallium",
    symbol: "Ga",
    riskLevel: "critical",
    topProducers: [
      { country: "China", flag: "🇨🇳", share: 98 },
      { country: "Japan", flag: "🇯🇵", share: 1 },
      { country: "South Korea", flag: "🇰🇷", share: 0.5 },
    ],
    applications: ["Semiconductors", "5G", "Military radar"],
    disruption: "China banned exports to US Dec 2024; near-total monopoly on production",
    supplyConcentration: 98,
  },
  {
    name: "Germanium",
    symbol: "Ge",
    riskLevel: "critical",
    topProducers: [
      { country: "China", flag: "🇨🇳", share: 60 },
      { country: "Belgium", flag: "🇧🇪", share: 20 },
      { country: "Canada", flag: "🇨🇦", share: 8 },
    ],
    applications: ["Fiber optics", "IR optics", "Night vision"],
    disruption: "Export restrictions from China since Aug 2023; defense supply chain at risk",
    supplyConcentration: 60,
  },
  {
    name: "Titanium",
    symbol: "Ti",
    riskLevel: "high",
    topProducers: [
      { country: "China", flag: "🇨🇳", share: 57 },
      { country: "Russia", flag: "🇷🇺", share: 13 },
      { country: "Japan", flag: "🇯🇵", share: 10 },
    ],
    applications: ["Aerospace", "Medical implants", "Naval vessels"],
    disruption: "Russia (VSMPO-AVISMA) sanctions impact Boeing & Airbus supply chains",
    supplyConcentration: 57,
  },
  {
    name: "Uranium",
    symbol: "U",
    riskLevel: "high",
    topProducers: [
      { country: "Kazakhstan", flag: "🇰🇿", share: 43 },
      { country: "Canada", flag: "🇨🇦", share: 15 },
      { country: "Namibia", flag: "🇳🇦", share: 11 },
    ],
    applications: ["Nuclear power", "Medical isotopes", "Naval propulsion"],
    disruption: "Russia enrichment dependency; US ban on Russian uranium imports signed 2024",
    supplyConcentration: 43,
  },
  {
    name: "Nickel",
    symbol: "Ni",
    riskLevel: "high",
    topProducers: [
      { country: "Indonesia", flag: "🇮🇩", share: 49 },
      { country: "Philippines", flag: "🇵🇭", share: 10 },
      { country: "Russia", flag: "🇷🇺", share: 8 },
    ],
    applications: ["Stainless steel", "EV batteries", "Aerospace alloys"],
    disruption: "Indonesian processing boom disrupting global pricing; environmental concerns",
    supplyConcentration: 49,
  },
  {
    name: "Copper",
    symbol: "Cu",
    riskLevel: "high",
    topProducers: [
      { country: "Chile", flag: "🇨🇱", share: 24 },
      { country: "DRC", flag: "🇨🇩", share: 12 },
      { country: "Peru", flag: "🇵🇪", share: 10 },
    ],
    applications: ["Wiring", "Renewables", "EVs", "Defense"],
    disruption: "Growing demand-supply gap; projected 10Mt shortfall by 2035",
    supplyConcentration: 24,
  },
  {
    name: "Tungsten",
    symbol: "W",
    riskLevel: "high",
    topProducers: [
      { country: "China", flag: "🇨🇳", share: 82 },
      { country: "Vietnam", flag: "🇻🇳", share: 5 },
      { country: "Russia", flag: "🇷🇺", share: 4 },
    ],
    applications: ["Armor-piercing ammo", "Cutting tools", "Electronics"],
    disruption: "China dominance; critical for defense manufacturing with no viable substitute",
    supplyConcentration: 82,
  },
  {
    name: "Palladium",
    symbol: "Pd",
    riskLevel: "medium",
    topProducers: [
      { country: "Russia", flag: "🇷🇺", share: 40 },
      { country: "South Africa", flag: "🇿🇦", share: 36 },
      { country: "Canada", flag: "🇨🇦", share: 8 },
    ],
    applications: ["Catalytic converters", "Electronics", "Hydrogen fuel cells"],
    disruption: "Sanctions uncertainty on Russian exports; South Africa power grid instability",
    supplyConcentration: 40,
  },
  {
    name: "Platinum",
    symbol: "Pt",
    riskLevel: "medium",
    topProducers: [
      { country: "South Africa", flag: "🇿🇦", share: 72 },
      { country: "Russia", flag: "🇷🇺", share: 12 },
      { country: "Zimbabwe", flag: "🇿🇼", share: 8 },
    ],
    applications: ["Fuel cells", "Catalysts", "Medical devices"],
    disruption: "South Africa loadshedding reduces mining output; hydrogen economy driving demand",
    supplyConcentration: 72,
  },
  {
    name: "Manganese",
    symbol: "Mn",
    riskLevel: "medium",
    topProducers: [
      { country: "South Africa", flag: "🇿🇦", share: 37 },
      { country: "Gabon", flag: "🇬🇦", share: 18 },
      { country: "Australia", flag: "🇦🇺", share: 14 },
    ],
    applications: ["Steel alloys", "EV batteries", "Aluminum cans"],
    disruption: "Gabon political instability; growing battery-grade demand from EV sector",
    supplyConcentration: 37,
  },
  {
    name: "Graphite",
    symbol: "C",
    riskLevel: "high",
    topProducers: [
      { country: "China", flag: "🇨🇳", share: 65 },
      { country: "Mozambique", flag: "🇲🇿", share: 13 },
      { country: "Madagascar", flag: "🇲🇬", share: 7 },
    ],
    applications: ["Battery anodes", "Lubricants", "Nuclear reactors"],
    disruption: "China export permits required since Dec 2023; critical EV battery anode material",
    supplyConcentration: 65,
  },
  {
    name: "Chromium",
    symbol: "Cr",
    riskLevel: "medium",
    topProducers: [
      { country: "South Africa", flag: "🇿🇦", share: 44 },
      { country: "Turkey", flag: "🇹🇷", share: 16 },
      { country: "Kazakhstan", flag: "🇰🇿", share: 13 },
    ],
    applications: ["Stainless steel", "Aerospace", "Armor plating"],
    disruption: "South Africa logistics bottlenecks; essential for stainless steel production",
    supplyConcentration: 44,
  },
];

const RISK_COLORS: Record<string, string> = {
  critical: "hsl(var(--destructive))",
  high: "hsl(var(--warning, 38 92% 50%))",
  medium: "hsl(var(--primary))",
};

const RISK_BADGE: Record<string, string> = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  high: "bg-warning/15 text-warning border-warning/30",
  medium: "bg-primary/15 text-primary border-primary/30",
};

const PIE_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--destructive))",
  "hsl(var(--warning, 38 92% 50%))",
  "hsl(var(--muted-foreground))",
];

const CriticalMaterialsMonitor = () => {
  const { isPremium } = useAuth();
  const [pricingOpen, setPricingOpen] = useState(false);
  const FREE_LIMIT = 5;

  const visibleMaterials = isPremium ? MATERIALS : MATERIALS.slice(0, FREE_LIMIT);

  const criticalCount = MATERIALS.filter((m) => m.riskLevel === "critical").length;
  const highCount = MATERIALS.filter((m) => m.riskLevel === "high").length;
  const avgConcentration = Math.round(MATERIALS.reduce((a, m) => a + m.supplyConcentration, 0) / MATERIALS.length);

  // Bar chart: supply concentration
  const concentrationData = visibleMaterials.map((m) => ({
    name: m.symbol,
    concentration: m.supplyConcentration,
    risk: m.riskLevel,
  }));

  // Pie chart: top producer country dominance
  const countryDominance = useMemo(() => {
    const map: Record<string, number> = {};
    MATERIALS.forEach((m) => {
      const top = m.topProducers[0];
      map[top.country] = (map[top.country] || 0) + 1;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
    const top3 = sorted.slice(0, 3).map(([name, value]) => ({ name, value }));
    const others = sorted.slice(3).reduce((s, [, v]) => s + v, 0);
    if (others > 0) top3.push({ name: "Others", value: others });
    return top3;
  }, []);

  return (
    <section className="border-b border-border py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1">
                <Gem className="h-3.5 w-3.5 text-primary" />
                <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-primary">
                  Critical Raw Materials Monitor
                </span>
              </div>
              <h2 className="text-xl font-bold text-foreground md:text-2xl">
                Strategic Resource Scarcity Tracker
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Tracking {MATERIALS.length} critical minerals — supply concentration, geopolitical risks &amp; disruptions
              </p>
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="border-destructive/30 text-destructive">
                {criticalCount} Critical
              </Badge>
              <Badge variant="outline" className="border-warning/30 text-warning">
                {highCount} High Risk
              </Badge>
            </div>
          </div>

          {/* Stats row */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { icon: Gem, label: "Materials Tracked", value: `${MATERIALS.length}` },
              { icon: AlertTriangle, label: "Critical Risk", value: `${criticalCount}` },
              { icon: Globe, label: "Avg. Concentration", value: `${avgConcentration}%` },
              { icon: Factory, label: "Top Monopolist", value: "China 🇨🇳" },
            ].map((s) => (
              <Card key={s.label} className="border-border bg-card">
                <CardContent className="flex items-center gap-3 p-4">
                  <s.icon className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-mono text-lg font-bold text-foreground">{s.value}</p>
                    <p className="text-[11px] text-muted-foreground">{s.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts */}
          <div className="mb-6 grid gap-4 md:grid-cols-2">
            <Card className="border-border bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  Supply Concentration (Top Producer %)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={concentrationData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} domain={[0, 100]} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v: number) => [`${v}%`, "Concentration"]}
                    />
                    <Bar dataKey="concentration" radius={[4, 4, 0, 0]}>
                      {concentrationData.map((entry, i) => (
                        <Cell key={i} fill={RISK_COLORS[entry.risk]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-border bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-primary" />
                  Dominant Producer Countries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={countryDominance}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={50}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value }) => `${name} (${value})`}
                      labelLine={false}
                    >
                      {countryDominance.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v: number) => [`${v} materials`, "Dominant in"]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Materials table */}
          <Card className="border-border bg-card overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Gem className="h-4 w-4 text-primary" />
                Critical Materials Database
                {!isPremium && (
                  <span className="ml-auto text-[10px] font-normal text-muted-foreground">
                    Showing {FREE_LIMIT} of {MATERIALS.length} — Upgrade for full access
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Material</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Risk</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Top Producers</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-muted-foreground md:table-cell">Applications</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-muted-foreground lg:table-cell">Latest Disruption</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMaterials.map((m, i) => (
                      <tr key={m.symbol} className={i < visibleMaterials.length - 1 ? "border-b border-border" : ""}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold text-primary">{m.symbol}</span>
                            <span className="font-medium text-foreground">{m.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-[10px] uppercase ${RISK_BADGE[m.riskLevel]}`}>
                            {m.riskLevel}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5">
                            {m.topProducers.map((p) => (
                              <span key={p.country} className="text-xs text-muted-foreground">
                                {p.flag} {p.country} <span className="font-mono text-foreground">{p.share}%</span>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="hidden px-4 py-3 md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {m.applications.map((a) => (
                              <span key={a} className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                {a}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="hidden px-4 py-3 lg:table-cell">
                          <p className="max-w-xs text-xs text-muted-foreground">{m.disruption}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Premium gate */}
              {!isPremium && (
                <div className="border-t border-border bg-primary/5 p-6 text-center">
                  <Lock className="mx-auto mb-2 h-5 w-5 text-primary" />
                  <p className="mb-1 text-sm font-semibold text-foreground">
                    {MATERIALS.length - FREE_LIMIT} more materials available with Premium
                  </p>
                  <p className="mb-3 text-xs text-muted-foreground">
                    Unlock full database including Tungsten, Palladium, Graphite analysis &amp; AI disruption alerts
                  </p>
                  <Button size="sm" onClick={() => setPricingOpen(true)} className="gap-2">
                    <Zap className="h-3.5 w-3.5" />
                    Unlock Full Access
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Intelligence notes */}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              {
                icon: Shield,
                title: "Defense Supply Risk",
                text: "China controls 98% of Gallium and 82% of Tungsten — both critical for military electronics and ammunition. Export bans create immediate defense procurement risks.",
              },
              {
                icon: Cpu,
                title: "Semiconductor Vulnerability",
                text: "Gallium & Germanium restrictions directly impact chip fabrication. TSMC, Intel, and Samsung face upstream raw material concentration risks from Chinese monopolies.",
              },
            ].map((note) => (
              <Card key={note.title} className="border-primary/20 bg-primary/5">
                <CardContent className="flex gap-3 p-4">
                  <note.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <p className="text-xs font-bold text-foreground">{note.title}</p>
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{note.text}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="mt-4 text-center text-[10px] text-muted-foreground">
            Data: USGS Mineral Commodity Summaries 2024, European Commission CRM List 2023, SIPRI • Updated periodically
          </p>
        </motion.div>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>
  );
};

export default CriticalMaterialsMonitor;
